import _ from 'lodash';
import {zodToJsonSchema} from 'zod-to-json-schema';

import {filterUndefined, isResponseFormat} from '../streaming';
import {
  CompletionResponseFormat,
  Message,
  SingleShotMessageRequest,
} from '../types';

/**
 * Convert messages to the expected format for the API.
 *
 * @param messages String or array of Message objects
 * @returns Array of Message objects
 */
export const prepareMessages = (
  messages: string | Array<Message>
): Message[] => {
  if (typeof messages === 'string') {
    return [
      {
        role: 'user',
        content: messages,
      },
    ];
  } else {
    return messages;
  }
};

/**
 * Prepare response format configuration for the API.
 *
 * @param responseFormat The desired response format
 * @returns Formatted response configuration
 */
export const prepareResponseFormat = (
  responseFormat?: CompletionResponseFormat
): any => {
  if (!responseFormat) {
    return undefined;
  }

  if (isResponseFormat(responseFormat)) {
    return responseFormat;
  }

  return {
    type: 'json_schema',
    json_schema: {
      name: 'response',
      strict: true,
      schema: _.omit(zodToJsonSchema(responseFormat), [
        '$schema',
        'definitions',
      ]),
    },
  };
};

/**
 * Prepare single-shot messages from a request object using a standard convention.
 *
 * This utility creates a properly formatted messages array for LLM completions
 * following a consistent pattern: system prompt → context → user message.
 */
export const prepareSingleShotMessages = (
  request: SingleShotMessageRequest
): Message[] => {
  const messages: Message[] = [];
  if (request.staticSystemPrompt) {
    messages.push({
      role: 'system',
      content: request.staticSystemPrompt,
    });
  }
  if (request.generationSpecificContext) {
    const filteredContext = filterUndefined(request.generationSpecificContext);
    let content = '## Context to take into consideration\n\n';

    for (const [key, value] of Object.entries(filteredContext)) {
      content += `### ${key}\n${JSON.stringify(value)}\n\n`;
    }

    messages.push({
      role: 'user',
      content: content.trim(),
    });
  }
  if (request.additionalUserPrompt) {
    messages.push({
      role: 'user',
      content: request.additionalUserPrompt,
    });
  }
  return messages;
};
